import { SiFacebook, SiInstagram, SiX } from '@icons-pack/react-simple-icons';
import { Clock, Linkedin, Mail, MapPin, Phone } from 'lucide-react';

const Footer = () => {
  const footerSections = [
    {
      title: 'Company',
      links: [
        { label: 'About', href: '/about' },
        { label: 'Brands', href: '/brands' },
        { label: 'Services', href: '/services' },
        { label: 'Operations', href: '/operations' },
        { label: 'Blog', href: '/blog' },
        { label: 'Contact', href: '/#cta' },
      ],
    },
    {
      title: 'Brand portfolio',
      links: [
        { label: 'Elzary', href: '/brands/elzary' },
        { label: 'Avyra Atlas', href: '/brands/avyraatlas' },
        { label: 'All brands', href: '/brands' },
      ],
    },
    {
      title: 'Services',
      links: [
        { label: 'Managed POD Operations', href: '/services' },
        { label: 'Brand Audit', href: '/services#tiers' },
        { label: 'Fractional Operator', href: '/services#tiers' },
      ],
    },
  ];

  const socialIcons = [
    {
      icon: <Linkedin size={20} aria-hidden />,
      href: 'https://www.linkedin.com/company/aitos-labs',
      label: 'LinkedIn',
      external: true,
    },
    {
      icon: <SiFacebook size={20} color="currentColor" aria-hidden />,
      href: 'https://www.facebook.com/aitos2026',
      label: 'Facebook',
      external: true,
    },
    {
      icon: <SiX size={20} color="currentColor" aria-hidden />,
      href: 'https://x.com/aitos2026',
      label: 'X (Twitter)',
      external: true,
    },
    {
      icon: <SiInstagram size={20} color="currentColor" aria-hidden />,
      href: 'https://www.instagram.com/aitos2026',
      label: 'Instagram',
      external: true,
    },
  ];

  return (
    <footer className="bg-gray-950 py-16 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-12">
          <div className="md:col-span-2">
            <a href="/" className="inline-block mb-6">
              <span className="text-3xl font-['Pacifico'] text-white">AITOS</span>
            </a>
            <p className="text-gray-300 mb-6 max-w-md">
              AITOS Labs LLC is a holding company for ecommerce Print-on-Demand brands. We build,
              own, and operate a portfolio of brands end-to-end — design, listing, fulfillment,
              customer experience, and growth — and offer the same operating stack to outside POD
              sellers as a managed service.
            </p>

            <div className="mb-6 space-y-3 text-gray-300">
              <a
                href="mailto:hi@aitos.app"
                className="flex items-center gap-2 hover:text-purple-400 transition-colors"
              >
                <Mail size={18} aria-hidden />
                <span>hi@aitos.app</span>
              </a>
              <a
                href="tel:+13074301688"
                className="flex items-center gap-2 hover:text-purple-400 transition-colors"
              >
                <Phone size={18} aria-hidden />
                <span>+1 (307) 430-1688</span>
              </a>
              <div className="flex items-center gap-2 text-sm">
                <Clock size={18} aria-hidden />
                <span>Mon–Fri, 9 AM–6 PM PT · Reply within 2 business days</span>
              </div>
              <address className="flex items-start gap-2 not-italic text-sm leading-relaxed">
                <MapPin size={18} aria-hidden className="mt-0.5 shrink-0" />
                <span>
                  AITOS Labs LLC
                  <br />
                  30 N Gould St Ste N
                  <br />
                  Sheridan, WY 82801, United States
                </span>
              </address>
              <p className="text-xs text-gray-500 leading-relaxed">
                AITOS Labs LLC is a limited liability company registered in the State of Wyoming,
                United States. Founded 2026.
              </p>
            </div>

            <div className="flex space-x-4">
              {socialIcons.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-300 hover:bg-purple-500 hover:text-white transition-colors"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {footerSections.map((section, index) => (
            <nav key={`footer-section-${index + 1}`} aria-label={section.title}>
              <p className="font-bold mb-6 text-white">{section.title}</p>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={`footer-link-${linkIndex + 1}`}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-purple-400 transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm text-center md:text-left">
            © 2026 AITOS Labs LLC. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/legal/terms"
              className="text-gray-400 hover:text-purple-400 transition-colors text-sm"
            >
              Terms
            </a>
            <a
              href="/legal/privacy"
              className="text-gray-400 hover:text-purple-400 transition-colors text-sm"
            >
              Privacy (GDPR &amp; CCPA)
            </a>
            <a
              href="/legal/privacy#ccpa"
              className="text-gray-400 hover:text-purple-400 transition-colors text-sm"
            >
              Do Not Sell My Info
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
